import React, { useEffect, useLayoutEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import Terms from '../pages/built-in/others/Terms'
import Error404Modern from '../pages/error/404-modern'
import Login from '../pages/auth/Login'
import Register from '../pages/auth/Register'
import Success from '../pages/auth/Success'
import Unsubscribe from '../pages/auth/Unsubscribe'
import Layout from '../layout/Index'
import LayoutWithSearch from '../layout/Index-withSearch'
import LayoutNoSidebar from '../layout/Index-nosidebar'
import Dashboard from '../pages/Dashboard'
import { useTranslation } from 'react-i18next'
import { t } from 'i18next'
import { LANGUAGES, setLanguageInURL } from '../utils/Utils'
import ImporterCompanies from '../pages/ImporterCompanies'
import PriceAnalysis from '../pages/PriceAnalysis'
import ProductAnalysis from '../pages/ProductAnalysis'
import MarketChanges from '../pages/MarketChanges'
import MarketOverview from '../pages/MarketOverview'
import { useGlobalStoreActions, useGlobalStoreData } from '../store/globalStore'
import McDataServices from '../services/McDataServices'
import { getUserDetailsAndProcess } from '../services/DataServiceMethods'
import SummaryView from '../pages/SummaryView'
import ForgotPassword from '../pages/auth/ForgotPassword'
import NewPassword from '../pages/auth/NewPassword'
import CustomsDuties from '../pages/CustomsDuties'
import PdfContainer from '../pages/PdfContainer'
import withAuth from '../hoc/withAuth'
import { isReferrerAllowed } from '../pages/mc-components/McPageHelpers'

// Mail Template imports
import MailTemplateLayout from '../mailTemplate/src/layouts/MailTemplateLayout'
import { getBooleanUserRolesByUser } from '../mailTemplate/src/utils/helpers'
import Campaigns from '../campaigns'
import CampaignDetailPage from '../campaigns/pages/CampaignDetailPage'
import Users from '../mailTemplate/src/components/atomic/pages/Users'
import Wizard from '../mailTemplate/src/components/atomic/pages/Wizard'
import CampaignPreparation from '../mailTemplate/src/components/atomic/pages/CampaignPreparation'
import Mailbox from '../mailTemplate/src/components/atomic/pages/Mailbox'
import Payment from '../pages/payment/Payment'
import PaymentLayout from '../layout/payment/PaymentLayout'
import PaymentResult from '../pages/payment/PaymentResult'
import UserProfile from '../pages/user/UserProfile'
import PersonnelMarketChanges from '../pages/personnel/PersonnelMarketChanges'
import Forecast from '../pages/Forecast'

import AdminLayout from '../layout/AdminLayout'
import AdminDashboard from '../pages/admin/AdminDashboard'

const LayoutWithAuth = withAuth(LayoutWithSearch)
const MailLayoutWithAuth = withAuth(MailTemplateLayout)
const PaymentLayoutWithAuth = withAuth(PaymentLayout)
const AdminLayoutWithAuth = withAuth(AdminLayout)

const Pages = ({ history }) => {
  const location = useLocation()
  const { i18n } = useTranslation()
  const languageGSD = useGlobalStoreData.language()
  const versionGSD = useGlobalStoreData.version()
  const userGSD = useGlobalStoreData.user()
  const isLoggedInGSD = useGlobalStoreData.isLoggedIn()

  const {
    setLoggedInStatus,
    setAppVersion,
    setLanguage,
    setUserData,
    setHsCodes
  } = useGlobalStoreActions()

  const userRoles = getBooleanUserRolesByUser(userGSD)

  const [tokenChecked, setTokenChecked] = useState(false)

  const screenScrollFixer = () => window.scrollTo(0, 0)
  const getAppVersion = () => {
    if (versionGSD === '') {
      McDataServices.getVersion().then(version => setAppVersion(version))
    }
    return () => { }
  }

  const tokenChecker = () => {
    setLanguageInURL(localStorage.getItem('lang'), true)

    const paymentPaths = ['/en/payment', '/tr/payment', '/ar/payment']

    if (isReferrerAllowed() && !paymentPaths.includes(location.pathname)) {
      McDataServices.logout().finally(() => {
        localStorage.setItem('username', '')
        getUserDetailsAndProcess(true, userGSD, setUserData)
        setTokenChecked(true)
      })
    } else {
      McDataServices.checkToken().then(result => {
        getUserDetailsAndProcess(!!result, userGSD, setUserData)
        setLoggedInStatus(!!result)
      }).finally(() => {
        setTokenChecked(true)
      })
    }
    return () => { }
  }
  const urlLanguageFixer = () => {
    const localStorageLang = localStorage.getItem('lang')

    if (languageGSD !== localStorageLang && localStorageLang !== null &&
      localStorageLang !== 'null') setLanguage(localStorage.getItem('lang'))
  }
  const languageFixer = () => {
    const selectedLang = localStorage.getItem('lang')
    i18n.changeLanguage(selectedLang)
    setLanguageInURL(selectedLang, false)

    const htmlElement = document.documentElement
    htmlElement.lang = selectedLang === LANGUAGES.ARABIC
      ? 'ar'
      : selectedLang === LANGUAGES.TURKISH ? 'tr' : 'en'
    htmlElement.dir = selectedLang === LANGUAGES.ARABIC ? 'rtl' : 'ltr'

    // document.body.style.textAlign = selectedLang === LANGUAGES.ARABIC ? 'left' : 'right'
    document.body.style.direction = selectedLang === LANGUAGES.ARABIC
      ? 'rtl'
      : 'ltr'
  }

  const pathnameFixer = () => {
    if (location.pathname === '/tr' || location.pathname === '/en' ||
      location.pathname === '/ar') {
      let predefinedSearchParams = new URL(window.location.href).searchParams
      predefinedSearchParams = predefinedSearchParams
        ? '?' +
        predefinedSearchParams.toString()
        : ''
      history?.push('/' + predefinedSearchParams)
    }
  }

  const getAndStoreHsCodes = () => {
    McDataServices.getHsCodes().then((res) => {
      setHsCodes(res)
    })
  }

  useLayoutEffect(screenScrollFixer, [location])
  useEffect(tokenChecker, [])
  useEffect(getAppVersion, [])
  useEffect(() => {
    languageFixer()
    getAndStoreHsCodes()
  }, [languageGSD])
  useEffect(urlLanguageFixer, [localStorage.getItem('lang')])
  useEffect(pathnameFixer, [location.pathname])

  return (
    <Routes>
      <Route path={`${process.env.PUBLIC_URL}`}>
        <Route element={<LayoutWithAuth tokenChecked={tokenChecked}/>}>
          <Route index element={<Dashboard userRoles={userRoles}/>}></Route>
          <Route path=":lang"
                 element={<Dashboard userRoles={userRoles}/>}></Route>
          <Route path=":lang/user-profile" element={<UserProfile/>}></Route>
          {/* {!userRoles.PERSONNEL && */}
          <Route path=":lang/personnel-market-changes"
                 element={<PersonnelMarketChanges
                   userRoles={userRoles}/>}></Route>
          {/* } */}
          {!userRoles.B2B_ONLY &&
            <>
              <Route path=":lang/importer-companies"
                     element={<ImporterCompanies/>}></Route>
              <Route path=":lang/price-analysis"
                     element={<PriceAnalysis/>}></Route>
              <Route path=":lang/product-analysis"
                     element={<ProductAnalysis/>}></Route>
              <Route path=":lang/market-changes"
                     element={<MarketChanges/>}></Route>
              <Route path=":lang/market-overview"
                     element={<MarketOverview/>}></Route>
              <Route path=":lang/summary-tables"
                     element={<SummaryView/>}></Route>
              <Route path=":lang/forecast"
                     element={<Forecast/>}></Route>
              <Route path=":lang/custom-duties"
                     element={<CustomsDuties/>}></Route>
              <Route path=":lang/pdf" element={<PdfContainer/>}></Route>
            </>
          }
        </Route>
      </Route>

      <Route path={`${process.env.PUBLIC_URL}`} element={<LayoutNoSidebar/>}>
        <Route path=":lang/success" element={<Success/>}></Route>
        <Route path=":lang/reset" element={<ForgotPassword/>}></Route>
        <Route path=":lang/new-password" element={<NewPassword/>}></Route>
        <Route path=":lang/register" element={<Register/>}></Route>
        <Route path=":lang/login" element={<Login/>}></Route>
        <Route path=":lang/unsubscribe" element={<Unsubscribe/>}></Route>
        <Route path="login" element={<Login/>}></Route>
        <Route path="*" element={
          isLoggedInGSD
            ? <Error404Modern/>
            : <Navigate
              to={`${process.env.PUBLIC_URL}/${languageGSD}/login`}
              replace
              state={{ sessionExpired: true }}
            />
        }></Route>
      </Route>

      <Route path={`${process.env.PUBLIC_URL}/:lang/register-external`}
             element={<Register/>}></Route>

      <Route path={`${process.env.PUBLIC_URL}`} element={<Layout/>}>
        <Route path=":lang/pages">
          <Route path="terms" element={<Terms/>}></Route>
        </Route>
      </Route>

      <Route path={`${process.env.PUBLIC_URL}`} element={<MailLayoutWithAuth
        title={t('mailTemplate.pageTitle')}/>}>
        {(userRoles.B2B) &&
          <>
            <Route path=":lang/campaigns">
              <Route index element={<Campaigns userGSD={userGSD}/>}/>
              <Route path="detail" element={<CampaignDetailPage/>}/>
              {userRoles.B2B_PERSONNEL &&
                <Route path="users" element={<Users userGSD={userGSD}/>}/>}
              <Route path="wizard" element={<Wizard/>}/>
              <Route path="preparation" element={<CampaignPreparation/>}/>
              <Route path="mailbox" element={<Mailbox/>}/>
            </Route>
          </>
        }
      </Route>

      <Route path={`${process.env.PUBLIC_URL}`}
             element={<PaymentLayoutWithAuth/>}>
        <Route path=":lang/payment">
          <Route index element={<Payment userRoles={userRoles}/>}/>
          <Route path="result"
                 element={<PaymentResult userRoles={userRoles}/>}/>
        </Route>
      </Route>

      <Route path={`${process.env.PUBLIC_URL}`}
             element={<AdminLayoutWithAuth tokenChecked={tokenChecked}/>}>
        {userRoles.ADMIN_PERSONNEL_USERS &&
          <Route path=":lang/admin">
            <Route index element={<AdminDashboard userRoles={userRoles}/>}/>
          </Route>
        }
      </Route>
    </Routes>
  )
}
export default Pages
